import { Directive, EventEmitter, Input, Output, ViewContainerRef, ComponentRef, OnInit, OnDestroy, HostListener } from "@angular/core";
import { Renderer2 } from "@angular/core";
import { Subject, debounceTime, filter, startWith, switchMap, takeUntil } from "rxjs";
import { skip } from "rxjs/operators";
// eslint-disable-next-line boundaries/dependencies
import { FileDropzoneComponent } from "../../shared/file-dropzone/file-dropzone.component";

@Directive({
    selector: "[kno2FileDropzoneOverlay]",
    standalone: false
})
export class FileDropzoneOverlayDirective implements OnInit, OnDestroy {
    private _dropzoneInstance: ComponentRef<FileDropzoneComponent>;
    private _isDropzoneDisabled: boolean;
    private _acceptedExtensions: Array<string>;
    private showOverlay$ = new Subject<boolean>();
    private destroy$ = new Subject<void>();
    @Input() public set acceptedExtensions(extensions: Array<string>) {
        this._acceptedExtensions = extensions;

        if (this._dropzoneInstance) this._dropzoneInstance.setInput("acceptedExtensions", this._acceptedExtensions);
    }
    @Input() public set isDropzoneDisabled(disabled: boolean) {
        this._isDropzoneDisabled = disabled;

        if (this._dropzoneInstance) this._dropzoneInstance.setInput("isDropzoneDisabled", this._isDropzoneDisabled);
    }
    @Output() public filesChanged = new EventEmitter<Array<File>>();

    constructor(
        private viwContainerRef: ViewContainerRef,
        private renderer2: Renderer2
    ) {}

    public ngOnInit(): void {
        const container = this.viwContainerRef.element.nativeElement;

        this._dropzoneInstance = this.viwContainerRef.createComponent(FileDropzoneComponent);

        this.renderer2.appendChild(container, this._dropzoneInstance.location.nativeElement);

        this.hideDropzoneOverlay();

        this._dropzoneInstance.setInput("acceptedExtensions", this._acceptedExtensions);
        this._dropzoneInstance.setInput("isDropzoneDisabled", this._isDropzoneDisabled);

        this.renderer2.addClass(this._dropzoneInstance.location.nativeElement, "hovered-dropzone");

        this._dropzoneInstance.changeDetectorRef.detectChanges();

        this._dropzoneInstance.instance.filesChanged.pipe(takeUntil(this.destroy$)).subscribe((files) => {
            this.showOverlay$.next(false);
            this.filesChanged.emit(files);
        });

        this.showOverlay$
            .pipe(
                takeUntil(this.destroy$),
                filter(Boolean),
                switchMap(() =>
                    // skips first value to prevent overlay from flickering
                    this.showOverlay$.pipe(
                        filter((x) => !x),
                        skip(1),
                        debounceTime(100),
                        startWith(true)
                    )
                )
            )
            .subscribe((showOverlay) => {
                if (showOverlay) return this.showDropzoneOverlay();

                this.hideDropzoneOverlay();
                this._dropzoneInstance.instance.files.set([]);
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        this._dropzoneInstance.destroy();
    }

    private hideDropzoneOverlay(): void {
        this.renderer2.setStyle(this._dropzoneInstance.location.nativeElement, "display", "none");
    }

    private showDropzoneOverlay(): void {
        this.renderer2.setStyle(this._dropzoneInstance.location.nativeElement, "display", "revert");
    }

    @HostListener("dragenter", ["$event"])
    public onDragEnter(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.showOverlay$.next(true);
    }

    @HostListener("dragover", ["$event"])
    public onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.showOverlay$.next(true);
    }

    @HostListener("dragleave", ["$event"])
    public onDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.showOverlay$.next(false);
    }
}
